/* ==========================================================================
   Daily Nouri — cart controller
   --------------------------------------------------------------------------
   Uses ONLY Shopify's own cart AJAX API and the Section Rendering API.
   No Recharge endpoints are called from the storefront; selling plans are
   swapped through /cart/change.js exactly as any Shopify selling plan is.
   ========================================================================== */

(function () {
  'use strict';

  // This file is referenced by BOTH dn-cart-drawer and dn-cart-page. On the
  // cart page both sections render, so guard against binding twice.
  if (window.DNCart) return;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var SECTIONS = ['dn-cart-drawer', 'dn-cart-page'];
  var UNDO_MS = 8000;

  var undoBuffer = null;   // { label, items: [addPayload, ...] }
  var undoTimer = null;
  var inFlight = false;

  /* ---------- helpers ---------------------------------------------------- */

  function url(path) {
    return ROOT.replace(/\/$/, '') + path;
  }

  function present(list) {
    // Only ask for sections that actually exist on this page.
    return list.filter(function (id) {
      return document.getElementById('shopify-section-' + id);
    });
  }

  function post(path, body) {
    var ids = present(SECTIONS);
    if (ids.length) body.sections = ids.join(',');
    return fetch(url(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw Object.assign(new Error(data.description || 'Cart error'), { data: data });
        return data;
      });
    });
  }

  function paint(data) {
    if (!data || !data.sections) return;
    // The drawer element lives INSIDE its section, so replacing the section's
    // innerHTML resets aria-hidden and would snap the drawer shut on every
    // cart update. Capture the open state and restore it.
    var d = drawer();
    var wasOpen = !!d && d.getAttribute('aria-hidden') === 'false';

    Object.keys(data.sections).forEach(function (id) {
      var host = document.getElementById('shopify-section-' + id);
      if (!host) return;
      var doc = new DOMParser().parseFromString(data.sections[id], 'text/html');
      var fresh = doc.getElementById('shopify-section-' + id);
      if (fresh) host.innerHTML = fresh.innerHTML;
    });

    if (wasOpen) {
      var next = drawer();
      if (next) {
        next.setAttribute('aria-hidden', 'false');
        document.documentElement.style.overflow = 'hidden';
      }
    }

    renderUndo();
    document.dispatchEvent(new CustomEvent('dn:cart:updated'));
  }

  function refresh() {
    var ids = present(SECTIONS);
    if (!ids.length) return Promise.resolve();
    return fetch(url('/?sections=' + ids.join(',')))
      .then(function (r) { return r.json(); })
      .then(function (sections) { paint({ sections: sections }); });
  }

  function busy(el, state) {
    var line = el && el.closest('[data-dn-line]');
    if (line) line.setAttribute('data-dn-busy', state ? 'true' : 'false');
  }

  /* ---------- quantity reconciliation -------------------------------------
     1 bottle = 1 month, so a subscription line's quantity MUST equal its
     plan's term. A double add-to-cart pushed quantity to 2/4/6 while the line
     stayed on the monthly plan, so the cart both displayed and charged
     monthly-price x N — e.g. $208.14 for a "6-month" line that should be
     $166.50. This repairs any line that has drifted, including carts already
     in the wild, and runs before the shopper sees a price.
     ---------------------------------------------------------------------- */

  var reconciling = false;

  function planTerm(name) {
    var m = String(name || '').match(/\d+/);
    var n = m ? parseInt(m[0], 10) : 1;
    return n > 0 ? n : 1;
  }

  /* Resolve the MONTHLY plan id for a variant from Shopify's /products/<handle>.js.
     Shape matters: plan names live in product.selling_plan_groups[].selling_plans[],
     and variant.selling_plan_allocations[] carries only selling_plan_id — NOT a
     nested selling_plan object. Reading a.selling_plan.name silently threw and
     the catch swallowed it, which is why plan-less lines were never repaired. */
  function monthlyPlanFromProduct(p, variantId) {
    var names = {};
    (p.selling_plan_groups || []).forEach(function (g) {
      (g.selling_plans || []).forEach(function (sp) { names[String(sp.id)] = sp.name || ''; });
    });
    var v = (p.variants || []).filter(function (x) { return String(x.id) === String(variantId); })[0];
    var best = null, bestMonths = Infinity;
    ((v && v.selling_plan_allocations) || []).forEach(function (a) {
      var id = a.selling_plan_id || (a.selling_plan && a.selling_plan.id);
      if (!id) return;
      var n = planTerm(names[String(id)] || (a.selling_plan && a.selling_plan.name) || '');
      if (n < bestMonths) { bestMonths = n; best = id; }
    });
    return best;
  }

  /* Zero-network path: the drawer section emits {variant_id: monthly_plan_id}
     for the current page's product (see dn-cart-drawer.liquid). */
  function monthlyPlanFromPage(variantId) {
    var el = document.querySelector('[data-dn-page-plans]');
    if (!el) return null;
    try { var m = JSON.parse(el.textContent || '{}'); return m[String(variantId)] || null; } catch (e) { return null; }
  }

  /* Cache of product handle -> monthly selling_plan id, so repeated
     reconciles don't re-fetch the same product JSON. */
  var monthlyPlanCache = {};

  function monthlyPlanFor(handle, variantId) {
    var fromPage = monthlyPlanFromPage(variantId);
    if (fromPage) return Promise.resolve(fromPage);
    var key = handle + ':' + variantId;
    if (monthlyPlanCache[key] !== undefined) return Promise.resolve(monthlyPlanCache[key]);
    return fetch(url('/products/' + handle + '.js'), { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (p) {
        var best = monthlyPlanFromProduct(p, variantId);
        monthlyPlanCache[key] = best;
        return best;
      })
      .catch(function () { monthlyPlanCache[key] = null; return null; });
  }

  function reconcile() {
    if (reconciling) return Promise.resolve(false);
    reconciling = true;

    return fetch(url('/cart.js'), { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = cart.items || [];

        /* 1. Lines with a plan but a drifted quantity (1 bottle = 1 month). */
        var qtyFixes = items.filter(function (i) {
          if (!i.selling_plan_allocation) return false;
          return i.quantity !== planTerm(i.selling_plan_allocation.selling_plan.name);
        });

        /* 2. Lines with NO plan at all. This store is subscription-only, so a
           plan-less line is always a failed add — whatever path added it
           (theme AJAX, Recharge widget, permalink, a stale form). Move it onto
           the product's monthly plan right here, where nothing can bypass us. */
        var planFixes = items.filter(function (i) { return !i.selling_plan_allocation && i.handle; });

        if (!qtyFixes.length && !planFixes.length) return false;

        var chain = Promise.resolve();
        qtyFixes.forEach(function (i) {
          chain = chain.then(function () {
            return post('/cart/change.js', {
              id: i.key,
              quantity: planTerm(i.selling_plan_allocation.selling_plan.name)
            });
          });
        });
        planFixes.forEach(function (i) {
          chain = chain.then(function () {
            return monthlyPlanFor(i.handle, i.variant_id).then(function (planId) {
              if (!planId) return null;
              return post('/cart/change.js', { id: i.key, quantity: 1, selling_plan: planId });
            });
          });
        });
        return chain.then(function () { return true; });
      })
      .catch(function () { return false; })
      .then(function (changed) {
        reconciling = false;
        return changed;
      });
  }

  /* ---------- drawer ------------------------------------------------------ */

  function drawer() { return document.querySelector('[data-dn-drawer]'); }

  function openDrawer() {
    var d = drawer();
    if (!d) return;
    d.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    var focusable = d.querySelector('button, [href], input');
    if (focusable) focusable.focus({ preventScroll: true });

    /* Repair a drifted quantity before the shopper reads a price. */
    reconcile().then(function (changed) { if (changed) refresh(); });
  }

  function closeDrawer() {
    var d = drawer();
    if (!d) return;
    d.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
  }

  /* ---------- undo -------------------------------------------------------- */

  function renderUndo() {
    document.querySelectorAll('[data-dn-undo-slot]').forEach(function (slot) {
      if (!undoBuffer) { slot.innerHTML = ''; return; }
      slot.innerHTML =
        '<div class="dn-undo"><span>' + undoBuffer.label + ' removed</span>' +
        '<button type="button" data-dn-undo>Undo</button></div>';
    });
  }

  function armUndo(label, items) {
    undoBuffer = { label: label, items: items };
    clearTimeout(undoTimer);
    undoTimer = setTimeout(function () { undoBuffer = null; renderUndo(); }, UNDO_MS);
    renderUndo();
  }

  function doUndo() {
    if (!undoBuffer) return;
    var items = undoBuffer.items;
    undoBuffer = null;
    clearTimeout(undoTimer);
    renderUndo();
    post('/cart/add.js', { items: items }).then(paint).catch(function () { refresh(); });
  }

  /* ---------- actions ------------------------------------------------------ */

  function changeCadence(btn) {
    var months = Number(btn.getAttribute('data-dn-cadence-months')) || 1;
    var map = {};
    try { map = JSON.parse(btn.getAttribute('data-dn-cadence-map') || '{}'); } catch (e) {}

    document.querySelectorAll('[data-dn-cadence-months]').forEach(function (b) {
      b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
    });

    // Every subscription line moves to ITS OWN plan for this term — each
    // product has different plan ids even for "the same" term, so we look
    // each one up individually rather than reusing a single id everywhere.
    // Reads straight off the DOM (data-dn-cadence-map already has every
    // variant's plan id for this term) — no extra /cart.js round trip.
    var lines = Array.prototype.slice.call(document.querySelectorAll('[data-dn-line][data-dn-plan]'))
      .filter(function (el) { return el.getAttribute('data-dn-plan') !== '0'; });

    var jobs = lines.map(function (el) {
      var key = el.getAttribute('data-dn-key');
      var variantId = el.getAttribute('data-dn-variant');
      var planId = map[variantId];
      return planId ? { key: key, planId: planId } : null;
    }).filter(Boolean);

    jobs.reduce(function (chain, job) {
      return chain.then(function () {
        return post('/cart/change.js', { id: job.key, quantity: months, selling_plan: job.planId });
      });
    }, Promise.resolve())
      .then(paint)
      .catch(function () { refresh(); });
  }

  function removeLine(btn) {
    var line = btn.closest('[data-dn-line]');
    if (!line) return;
    var label = line.getAttribute('data-dn-title') || 'Item';
    var keys = [line.getAttribute('data-dn-key')];
    var partner = line.getAttribute('data-dn-partner-key');
    if (partner) keys.push(partner);

    busy(btn, true);

    // Capture enough to put it back exactly as it was.
    fetch(url('/cart.js'))
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var restore = cart.items
          .filter(function (i) { return keys.indexOf(i.key) !== -1; })
          .map(function (i) {
            var payload = { id: i.variant_id, quantity: i.quantity };
            if (i.selling_plan_allocation) {
              payload.selling_plan = i.selling_plan_allocation.selling_plan.id;
            }
            // Preserve line properties on undo, minus Recharge's legacy
            // interval keys (see addFromForm) so we never resurrect stale ones.
            if (i.properties && Object.keys(i.properties).length) {
              var kept = {};
              Object.keys(i.properties).forEach(function (k) {
                if (!/^(shipping_interval|charge_interval|subscription_id|_rc_)/i.test(k)) kept[k] = i.properties[k];
              });
              if (Object.keys(kept).length) payload.properties = kept;
            }
            return payload;
          });

        var updates = {};
        keys.forEach(function (k) { updates[k] = 0; });

        return post('/cart/update.js', { updates: updates }).then(function (data) {
          armUndo(label, restore);
          paint(data);
        });
      })
      .catch(function () { refresh(); });
  }

  function addCrossSell(btn) {
    var variantId = Number(btn.getAttribute('data-variant') || btn.getAttribute('data-dn-variant'));
    if (!variantId) return;
    btn.disabled = true;
    var originalLabel = btn.textContent;
    btn.textContent = 'Adding…';

    // Each product has its OWN distinct selling_plan ids, even for "the
    // same" term — use the plan/months rendered specifically for THIS
    // product (matched server-side), not the hero line's plan id.
    var planId = Number(btn.getAttribute('data-dn-crosssell-plan')) || null;
    var months = Number(btn.getAttribute('data-dn-crosssell-months')) || 1;

    if (!planId) {
      var hero = document.querySelector('[data-dn-line]');
      planId = hero ? Number(hero.getAttribute('data-dn-plan')) || null : null;
      months = hero ? Number(hero.getAttribute('data-dn-months')) || 1 : months;
    }

    var payload = { id: variantId, quantity: months };
    if (planId) payload.selling_plan = planId;

    post('/cart/add.js', { items: [payload] })
      .then(paint)
      .catch(function () {
        btn.disabled = false;
        btn.textContent = originalLabel;
        refresh();
      });
  }

  function applyCode(form) {
    var input = form.querySelector('input[name="discount"]');
    var errorEl = form.parentNode.querySelector('[data-dn-code-error]');
    var code = (input.value || '').trim();
    if (!code) return;
    if (errorEl) errorEl.hidden = true;

    // Shopify has no AJAX discount endpoint; hitting /discount/CODE sets the
    // cart's discount, then we re-render. See INSTALL.md — verify on staging.
    fetch(url('/discount/' + encodeURIComponent(code)), { method: 'GET' })
      .then(function () { return fetch(url('/cart.js')); })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var ok = cart.cart_level_discount_applications &&
                 cart.cart_level_discount_applications.length > 0;
        if (!ok && errorEl) errorEl.hidden = false;
        return refresh();
      })
      .catch(function () { if (errorEl) errorEl.hidden = false; });
  }

  /* ---------- wiring -------------------------------------------------------- */

  document.addEventListener('click', function (e) {
    var t = e.target;

    /* Progressive disclosure: reveal the full delivery list only on request. */
    var toggle = t.closest && t.closest('[data-dn-cadence-toggle]');
    if (toggle) {
      e.preventDefault();
      var wrap = toggle.closest('.dn-cadence');
      var panel = wrap && wrap.querySelector('[data-dn-cadence-panel]');
      if (panel) {
        var open = panel.hasAttribute('hidden');
        if (open) { panel.removeAttribute('hidden'); } else { panel.setAttribute('hidden', ''); }
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
      return;
    }

    var cadence = t.closest && t.closest('[data-dn-cadence-months]');
    if (cadence) { e.preventDefault(); if (!inFlight) changeCadence(cadence); return; }

    var rm = t.closest && t.closest('[data-dn-remove]');
    if (rm) { e.preventDefault(); removeLine(rm); return; }

    var undo = t.closest && t.closest('[data-dn-undo]');
    if (undo) { e.preventDefault(); doUndo(); return; }

    var cross = t.closest && t.closest('[data-dn-add-crosssell]');
    if (cross) { e.preventDefault(); addCrossSell(cross); return; }

    var close = t.closest && t.closest('[data-dn-close]');
    if (close) { e.preventDefault(); closeDrawer(); return; }
  });

  document.addEventListener('submit', function (e) {
    var form = e.target.closest && e.target.closest('[data-dn-code-form]');
    if (!form) return;
    e.preventDefault();
    applyCode(form);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeDrawer();
  });

  /* ---------- PDP: subscription-first, no frequency UI ----------------------
     The term is chosen in the cart now, so the PDP widget is hidden. Before
     hiding it we select the subscription option, so "Start Now" adds a
     subscription by default instead of a one-time purchase. Reading the plan
     from the form means no hardcoded IDs — this works for every product.
     ------------------------------------------------------------------------ */

  function subscriptionFirst() {
    document.querySelectorAll('form[action*="/cart/add"]').forEach(function (form) {
      var plan = null;

      /* Recharge's widget radios (rc-radio__input) carry 'onetime' for the
         one-off option; anything else is a subscription plan. */
      form.querySelectorAll('input.rc-radio__input[type="radio"]').forEach(function (r) {
        if (!plan && r.value && r.value !== 'onetime') plan = r;
      });

      /* Fall back to Shopify-native selling plan radios. */
      if (!plan) {
        form.querySelectorAll('input[name="selling_plan"]').forEach(function (r) {
          if (!plan && r.value && r.value !== '0') plan = r;
        });
      }

      if (!plan || plan.checked) return;

      plan.checked = true;
      plan.dispatchEvent(new Event('change', { bubbles: true }));
      plan.dispatchEvent(new Event('input', { bubbles: true }));
      plan.dispatchEvent(new Event('click', { bubbles: true }));
    });
  }

  document.addEventListener('DOMContentLoaded', subscriptionFirst);
  /* Recharge renders its widget asynchronously; catch it when it appears. */
  document.addEventListener('DOMContentLoaded', function () {
    var tries = 0;
    var poll = setInterval(function () {
      subscriptionFirst();
      if (++tries > 20) clearInterval(poll);
    }, 250);
  });

  /* Open the drawer after any theme add-to-cart that we can observe. */
  document.addEventListener('dn:cart:open', openDrawer);

  /* Repair carts that are ALREADY broken in the wild: a returning shopper may
     have a line sitting at quantity 4 or 6 on the monthly plan. Fix it on load
     so the drawer, the cart page and checkout all agree. */
  document.addEventListener('DOMContentLoaded', function () {
    reconcile().then(function (changed) { if (changed) refresh(); });
  });

  /* ---------- own the add-to-cart ------------------------------------------
     The theme delegated add-to-cart to an app drawer that is now disabled, so
     no handler remains. We intercept the product form ourselves: add via AJAX,
     then open the drawer. Works on PDP forms and sticky bars alike.
     ------------------------------------------------------------------------ */

  function addFromForm(form, btn) {
    if (inFlight) return;
    inFlight = true;

    btn = btn || form.querySelector('[type="submit"], [name="add"]');
    var label = btn ? btn.innerHTML : null;
    if (btn) { btn.disabled = true; btn.innerHTML = 'Adding…'; }

    var data = new FormData(form);
    /* One tap = one bottle. Quantity is no longer a shopper choice — the term
       is picked in the cart — and /cart/add.js INCREMENTS an existing line, so
       without this a second tap silently became a "2-month" line. */
    data.set('quantity', '1');

    /* Strip Recharge's LEGACY widget properties. The hidden rc_widget still
       emits properties[shipping_interval_frequency] etc. from the PDP form.
       On Shopify Checkout Integration the selling_plan alone drives the
       subscription; these properties are dead weight that (a) print as raw
       key:value text on the checkout line and (b) go stale the moment the
       cadence changes ("30 Days" on a 3-month plan). */
    Array.prototype.slice.call(data.keys()).forEach(function (k) {
      if (/^properties\[(shipping_interval|charge_interval|subscription_id|_rc_)/i.test(k)) data.delete(k);
    });

    var ids = present(SECTIONS);
    if (ids.length) data.append('sections', ids.join(','));

    /* GUARANTEE a subscription. The hidden Recharge widget is supposed to
       supply selling_plan, but when it doesn't (markup change, race, the
       radio not yet mounted) the add lands as a ONE-TIME purchase: checkout
       shows no plan, and the cadence control — which needs a subscription
       line to build from — renders nothing. Resolve the monthly plan for this
       variant from the product JSON and attach it ourselves. */
    var ensurePlan = Promise.resolve();
    var sp = data.get('selling_plan');
    if (!sp || sp === '0' || sp === '') {
      var variantId = String(data.get('id') || '');
      var m = window.location.pathname.match(/\/products\/([^\/?#]+)/);
      if (variantId) {
        ensurePlan = monthlyPlanFor(m ? m[1] : '', variantId).then(function (best) {
          if (best) data.set('selling_plan', String(best));
        });
      }
    }

    ensurePlan.then(function () {
      return fetch(url('/cart/add.js'), {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: data
      });
    })
      .then(function (res) { return res.json(); })
      .then(function (payload) {
        if (payload.status && payload.status !== 200) throw new Error(payload.description);
        /* Repair quantity BEFORE painting, so no wrong price is ever shown. */
        return reconcile().then(function (changed) {
          if (changed) return refresh();
          if (payload.sections) { paint(payload); return null; }
          return refresh();
        });
      })
      .then(function () { openDrawer(); })
      .catch(function () { form.submit(); })
      .then(function () {
        inFlight = false;
        if (btn) { btn.disabled = false; if (label !== null) btn.innerHTML = label; }
      });
  }

  function isAddForm(form) {
    if (!form || !form.getAttribute) return false;
    return (form.getAttribute('action') || '').indexOf('/cart/add') !== -1;
  }

  /* Capture-phase submit: beats the theme's own submit handler. */
  document.addEventListener('submit', function (e) {
    if (!isAddForm(e.target)) return;
    e.preventDefault();
    e.stopPropagation();
    addFromForm(e.target);
  }, true);

  /* Capture-phase click: many themes bind the button directly and never let a
     submit event fire at all, which is why nothing happened before. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[name="add"], .product-add button, button[type="submit"]');
    if (!btn) return;
    var form = btn.form || btn.closest('form');
    if (!isAddForm(form)) return;
    e.preventDefault();
    e.stopPropagation();
    addFromForm(form, btn);
  }, true);

  /* Quiz hand-off: /cart?dn_open=1 (or #dn-cart) opens the sheet on arrival. */
  document.addEventListener('DOMContentLoaded', function () {
    var params = new URLSearchParams(window.location.search);
    if (params.get('dn_open') === '1' || window.location.hash === '#dn-cart') {
      openDrawer();
    }
  });

  window.DNCart = {
    open: openDrawer,
    close: closeDrawer,
    refresh: refresh
  };
})();
